/**
 * ParticipantsList — Liste mit allen Teilnehmer-Cards (für Expand der
 * ParticipantsBar). Rolle-aware Aktionen.
 */

import { useState, type FormEvent } from "react";
import { Plus } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { Input, Select } from "@/components/Input";
import { EventParticipantCard } from "./EventParticipantCard";
import type { Participant } from "@/lib/api";
import styles from "./ParticipantsList.module.css";

type AdminProps = {
  viewerRole: "admin";
  participants: Participant[];
  onAdd: (payload: { name: string; persons: number }) => void;
  onSave: (id: number, payload: { name?: string; participates?: boolean; persons?: number }) => void;
  onDelete: (id: number) => void;
  isAddPending?: boolean;
};

type UserProps = {
  viewerRole: "user";
  participants: Participant[];
  selfId: number;
  onSaveSelf: (payload: { name?: string; participates?: boolean; persons?: number }) => void;
};

type GuestProps = {
  viewerRole: "guest";
  participants: Participant[];
};

export type ParticipantsListProps = AdminProps | UserProps | GuestProps;

export function ParticipantsList(props: ParticipantsListProps) {
  return (
    <div className={styles.wrap}>
      <div className={styles.list}>
        {props.participants.length === 0 ? (
          <Empty />
        ) : (
          props.participants.map((p) => renderCard(p, props))
        )}
      </div>
      {props.viewerRole === "admin" && (
        <AddParticipantForm onAdd={props.onAdd} isPending={props.isAddPending} />
      )}
    </div>
  );
}

function renderCard(p: Participant, props: ParticipantsListProps) {
  if (props.viewerRole === "admin") {
    return (
      <EventParticipantCard
        key={p.id}
        viewerRole="admin"
        participant={p}
        onSave={props.onSave}
        onDelete={props.onDelete}
      />
    );
  }
  if (props.viewerRole === "user") {
    return (
      <EventParticipantCard
        key={p.id}
        viewerRole="user"
        participant={p}
        isSelf={p.id === props.selfId}
        onSaveSelf={p.id === props.selfId ? props.onSaveSelf : undefined}
      />
    );
  }
  return <EventParticipantCard key={p.id} viewerRole="guest" participant={p} />;
}

function Empty() {
  const { t } = useTranslation();
  return <div className={styles.empty}>{t("admin.noParticipantsYet")}</div>;
}

function AddParticipantForm({
  onAdd,
  isPending,
}: {
  onAdd: AdminProps["onAdd"];
  isPending?: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [persons, setPersons] = useState(1);

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) return;
    onAdd({ name: name.trim(), persons });
    setName("");
    setPersons(1);
  };

  return (
    <form className={styles.addForm} onSubmit={submit}>
      <Input
        label={t("admin.participantName")}
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={50}
      />
      <Select
        label={t("admin.participantPersons")}
        value={persons}
        onChange={(e) => setPersons(Number(e.target.value))}
      >
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </Select>
      <Button type="submit" size="compact" disabled={!name.trim() || isPending}>
        <Plus size={16} weight="regular" />
        {t("admin.addParticipant")}
      </Button>
    </form>
  );
}
